import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaAward,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaGraduationCap,
  FaLaptopCode,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegCalendarAlt,
  FaSchool,
  FaShieldAlt,
  FaTrophy,
  FaUserGraduate,
  FaUsers,
  FaChevronRight,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { useTenant } from "../context/TenantContext";
import { useAppTheme } from "../context/AppThemeContext";
import Seo from "../components/Seo";
import { loadTenantPortalContent } from "../utils/tenantPortalContent";

export default function PublicTenantHome({ userRole }) {
  const {
    activeTenant,
    tenantLogoUrl,
    schoolName: contextSchoolName,
    subdomain,
  } = useTenant();

  let isDarkMode = false;
  try {
    const theme = useAppTheme();
    isDarkMode = Boolean(theme?.isDarkMode);
  } catch {
    isDarkMode = false;
  }

  const schoolName =
    contextSchoolName ||
    activeTenant?.schoolName ||
    activeTenant?.name ||
    (subdomain ? subdomain.toUpperCase() : "Our School");
  const tenantKey = subdomain || activeTenant?.id || "default";

  const portalData = useMemo(
    () => loadTenantPortalContent(tenantKey, schoolName),
    [tenantKey, schoolName]
  );

  const schoolInitials = useMemo(() => {
    return (
      schoolName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase() || "SC"
    );
  }, [schoolName]);

  const [selectedAchievementCategory, setSelectedAchievementCategory] = useState("All Honors");
  const [activeRolePreview, setActiveRolePreview] = useState("student");
  const [copiedText, setCopiedText] = useState("");

  const handleCopy = (text, label) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedText(label);
      setTimeout(() => setCopiedText(""), 2000);
    }
  };

  const achievementCategories = useMemo(() => {
    const set = new Set(["All Honors"]);
    (portalData.achievements || []).forEach((a) => {
      if (a.category) set.add(a.category);
    });
    return Array.from(set);
  }, [portalData.achievements]);

  const filteredAchievements = useMemo(() => {
    if (selectedAchievementCategory === "All Honors") return portalData.achievements || [];
    return (portalData.achievements || []).filter(
      (a) => a.category?.toLowerCase() === selectedAchievementCategory.toLowerCase()
    );
  }, [portalData.achievements, selectedAchievementCategory]);

  const effectiveLogo = tenantLogoUrl || activeTenant?.logoUrl || activeTenant?.schoolLogoUrl;

  const rolePortals = [
    {
      id: "student",
      title: "Student Portal",
      desc: "Course materials, classroom discussions, assignments & exam results.",
      icon: FaUserGraduate,
      badge: "Learner Workspace",
      gradient: "from-sky-500 via-blue-600 to-indigo-700",
      glowColor: "rgba(56, 189, 248, 0.25)",
      accentText: "text-sky-400",
      accentBg: "bg-sky-500/10 text-sky-400 border-sky-500/20",
      tags: ["Digital LMS", "Exam Results", "Class Schedule", "Assignments"],
    },
    {
      id: "faculty",
      title: "Faculty Portal",
      desc: "Attendance tracking, grading, lesson syllabus & live classes.",
      icon: FaChalkboardTeacher,
      badge: "Teacher Workspace",
      gradient: "from-indigo-500 via-purple-600 to-pink-600",
      glowColor: "rgba(168, 85, 247, 0.25)",
      accentText: "text-purple-400",
      accentBg: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      tags: ["Lesson Plans", "Gradebook", "Roll Call", "Live Classes"],
    },
    {
      id: "parent",
      title: "Parent Portal",
      desc: "Academic progress, fee statements, online receipts & official notices.",
      icon: FaUsers,
      badge: "Family Workspace",
      gradient: "from-emerald-500 via-teal-600 to-cyan-700",
      glowColor: "rgba(52, 211, 153, 0.25)",
      accentText: "text-emerald-400",
      accentBg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      tags: ["Fee Statements", "Report Cards", "Direct Messages", "Circulars"],
    },
    {
      id: "admin",
      title: "Admin & Staff",
      desc: "Comprehensive admissions, scheduling, finance & school analytics.",
      icon: FaShieldAlt,
      badge: "Operations Hub",
      gradient: "from-amber-500 via-orange-600 to-rose-600",
      glowColor: "rgba(251, 191, 36, 0.25)",
      accentText: "text-amber-400",
      accentBg: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      tags: ["Admissions CRM", "SIS Matrix", "Audit Logs", "Analytics"],
    },
  ];

  return (
    <div
      className={`min-h-full w-full font-sans antialiased selection:bg-blue-500 selection:text-white transition-colors duration-300 ${
        isDarkMode ? "bg-[#070b14] text-slate-100" : "bg-slate-50 text-slate-900"
      }`}
    >
      <Seo
        title={`${schoolName} | Official Campus Portal`}
        description={`Welcome to ${schoolName}. Access academic programs, campus announcements, and the secure student, faculty, and parent portal.`}
        canonical="/"
      />

      {/* ========================================================
          HERO SECTION: CINEMATIC CAMPUS EXPERIENCE
      ======================================================== */}
      <section className="relative isolate overflow-hidden border-b border-white/10 bg-[#070d1e] text-white">
        {/* Ambient atmospheric aura & subtle futuristic grid texture */}
        <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-screen">
          <div className="absolute -top-40 left-1/4 h-[550px] w-[550px] rounded-full bg-blue-600/30 blur-[130px]" />
          <div className="absolute -bottom-20 right-1/4 h-[500px] w-[500px] rounded-full bg-cyan-500/20 blur-[120px]" />
          <div className="absolute top-1/2 left-2/3 h-[450px] w-[450px] rounded-full bg-purple-600/20 blur-[140px]" />
        </div>
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.035]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-5 pt-16 pb-20 sm:px-6 sm:pt-20 sm:pb-24 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            {/* Left Column: Hero Narrative & Identity */}
            <div>
              {/* Premium Official Verified Badge */}
              <div className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/30 bg-cyan-950/40 px-4 py-1.5 text-xs font-semibold tracking-wide text-cyan-300 shadow-inner backdrop-blur-xl">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <FaShieldAlt className="text-cyan-300" />
                <span className="font-bold uppercase tracking-wider text-[11px]">
                  Official Campus Portal
                </span>
                <span className="h-1 w-1 rounded-full bg-cyan-400/50" />
                <span className="text-cyan-200">Verified School</span>
              </div>

              {/* Majestic School Welcome Headline */}
              <h1 className="mt-6 text-3xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.1]">
                <span className="block text-slate-300 text-lg sm:text-2xl font-semibold mb-1">
                  Welcome to
                </span>
                <span className="bg-gradient-to-r from-white via-cyan-100 to-blue-300 bg-clip-text text-transparent">
                  {schoolName}
                </span>
              </h1>

              {/* Tagline */}
              <p className="mt-4 text-base font-medium text-cyan-200/90 sm:text-xl leading-snug">
                {portalData.tagline}
              </p>

              {/* Welcoming Mission Statement */}
              <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-slate-300/80">
                {portalData.welcomeMessage}
              </p>

              {/* Hero Action CTA Buttons */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/login"
                  className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-500 px-8 py-4 text-sm font-bold text-white shadow-xl shadow-blue-500/25 transition-all duration-300 hover:scale-[1.02] hover:shadow-cyan-500/35 active:scale-[0.98]"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <span>Sign In to Portal</span>
                    <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                </Link>

                <a
                  href="#portals"
                  className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.07] px-6 py-4 text-sm font-semibold text-white shadow-sm backdrop-blur-xl transition hover:border-cyan-400/50 hover:bg-white/[0.12] active:scale-[0.98]"
                >
                  <FaUserGraduate className="text-cyan-300" />
                  <span>Access Your {schoolName} Workspace</span>
                </a>
              </div>

              {/* Verified School Stats & Accreditations Ribbon */}
              <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4 border-t border-white/10 pt-8">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-md">
                  <div className="text-lg font-black text-amber-400">100%</div>
                  <div className="text-[11px] font-medium text-slate-300">Board Exam Pass Rate</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-md">
                  <div className="text-lg font-black text-cyan-400">14:1</div>
                  <div className="text-[11px] font-medium text-slate-300">Student-Faculty Ratio</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-md">
                  <div className="text-lg font-black text-emerald-400">{portalData.establishedYear || "2015"}</div>
                  <div className="text-[11px] font-medium text-slate-300">Established Year</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-md">
                  <div className="text-lg font-black text-indigo-400">Verified</div>
                  <div className="text-[11px] font-medium text-slate-300 truncate" title={portalData.affiliation}>
                    {portalData.affiliation ? "Accredited Campus" : "Digital Campus"}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Next-Gen Institutional Terminal Card */}
            <div className="relative">
              {/* Outer decorative glow */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-blue-500/30 via-cyan-500/20 to-purple-500/30 blur-xl opacity-75" />

              <div className="relative rounded-3xl border border-white/20 bg-gradient-to-b from-white/[0.12] to-white/[0.03] p-7 sm:p-8 shadow-2xl backdrop-blur-2xl">
                {/* School Header Badge with Monogram */}
                <div className="flex items-center gap-4">
                  {effectiveLogo ? (
                    <img
                      src={effectiveLogo}
                      alt={`${schoolName} logo`}
                      className="h-16 w-16 rounded-2xl object-cover ring-2 ring-cyan-400/40 shadow-xl"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-500 text-2xl font-black text-white ring-2 ring-white/30 shadow-xl">
                      {schoolInitials}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                        Campus Network Online
                      </span>
                    </div>
                    <h2 className="truncate text-lg font-black text-white sm:text-xl">
                      {schoolName}
                    </h2>
                    <p className="truncate text-xs text-cyan-200/80">
                      {subdomain ? `${subdomain}.classnx.com` : "Secure Cloud Campus"}
                    </p>
                  </div>
                </div>

                {/* Interactive Portal Quick Selector Tabs */}
                <div className="mt-6 rounded-2xl bg-black/30 p-1 border border-white/10">
                  <div className="grid grid-cols-4 gap-1 text-[11px] font-bold">
                    {rolePortals.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setActiveRolePreview(r.id)}
                        className={`rounded-xl py-2 px-1 text-center transition-all ${
                          activeRolePreview === r.id
                            ? "bg-white text-slate-950 shadow-md font-extrabold"
                            : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {r.title.split(" ")[0]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Role Highlights Box */}
                {(() => {
                  const currentRole = rolePortals.find((r) => r.id === activeRolePreview) || rolePortals[0];
                  const Icon = currentRole.icon;
                  return (
                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.05] p-4 text-xs">
                      <div className="flex items-center gap-2 text-cyan-300 font-bold">
                        <Icon className="text-sm" />
                        <span>{currentRole.title} Environment</span>
                      </div>
                      <p className="mt-1.5 text-slate-300 leading-relaxed text-[11px]">
                        {currentRole.desc}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {currentRole.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-white/10 px-2.5 py-0.5 text-[10px] font-medium text-slate-200"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Direct Action Link */}
                <div className="mt-6">
                  <Link
                    to="/login"
                    className="block w-full rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 py-3.5 text-center text-xs font-black uppercase tracking-wider text-slate-950 shadow-lg shadow-cyan-500/25 transition-all hover:opacity-95 hover:shadow-cyan-400/40 active:scale-[0.99]"
                  >
                    Sign In to Campus Workspace
                  </Link>
                </div>

                {/* Helpline quick summary */}
                <div className="mt-4 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <FaPhoneAlt className="text-cyan-400 text-[10px]" />
                    <span>{portalData.contactPhone}</span>
                  </span>
                  <span className="font-semibold text-emerald-400 flex items-center gap-1">
                    <FaShieldAlt className="text-[10px]" /> 256-Bit SSL Secured
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================
          ROLE PORTALS DIRECTORY SECTION
      ======================================================== */}
      <section id="portals" className="mx-auto max-w-7xl px-5 py-20 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-blue-600 dark:bg-blue-400/10 dark:text-blue-400 border border-blue-500/20">
            <FaLaptopCode />
            <span>Secure Role Portals</span>
          </div>
          <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
            Access Your {schoolName} Workspace
          </h2>
          <p
            className={`mt-3 text-sm sm:text-base leading-relaxed ${
              isDarkMode ? "text-slate-400" : "text-slate-600"
            }`}
          >
            Streamlined, secure digital workspaces tailored for each member of our campus
            community. Select your portal to proceed to single sign-on.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {rolePortals.map((portal) => {
            const Icon = portal.icon;
            return (
              <Link
                key={portal.title}
                to="/login"
                className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border p-6 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl ${
                  isDarkMode
                    ? "border-slate-800 bg-[#0c1222] hover:border-blue-500/50 hover:shadow-blue-500/10"
                    : "border-slate-200/90 bg-white hover:border-blue-400 hover:shadow-blue-500/10"
                }`}
              >
                {/* Hover subtle glow background */}
                <div
                  className="absolute -right-16 -top-16 h-36 w-36 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100 pointer-events-none"
                  style={{ backgroundColor: portal.glowColor }}
                />

                <div>
                  <div className="flex items-center justify-between">
                    <div
                      className={`inline-flex h-13 w-13 items-center justify-center rounded-2xl bg-gradient-to-br ${portal.gradient} p-3.5 text-xl text-white shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}
                    >
                      <Icon />
                    </div>
                    <span
                      className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${portal.accentBg}`}
                    >
                      {portal.badge}
                    </span>
                  </div>

                  <h3 className="mt-5 text-lg font-black tracking-tight">{portal.title}</h3>
                  <p
                    className={`mt-2 text-xs leading-relaxed ${
                      isDarkMode ? "text-slate-400" : "text-slate-600"
                    }`}
                  >
                    {portal.desc}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {portal.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`rounded-lg px-2 py-0.5 text-[10px] font-medium ${
                          isDarkMode
                            ? "bg-slate-800/80 text-slate-300"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4 text-xs font-bold text-blue-600 dark:border-slate-800/80 dark:text-cyan-400">
                  <span className="group-hover:underline">Enter Workspace</span>
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ========================================================
          HIGHLIGHTS & ANNOUNCEMENTS (EXECUTIVE DUAL SHOWCASE)
      ======================================================== */}
      <section
        className={`border-t py-16 sm:py-20 ${
          isDarkMode
            ? "border-slate-800/80 bg-[#090e1c]"
            : "border-slate-200/80 bg-slate-100/70"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2">
            {/* Left Card: School Achievements & Milestones */}
            <div
              className={`rounded-3xl border p-7 sm:p-8 transition-all ${
                isDarkMode
                  ? "border-slate-800/90 bg-[#0d1426] shadow-xl"
                  : "border-slate-200 bg-white shadow-lg"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-500/20 shadow-sm">
                    <FaTrophy className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black tracking-tight">
                      School Achievements & Milestones
                    </h3>
                    <p className="text-xs text-slate-400">Honors, recognitions, and awards</p>
                  </div>
                </div>
              </div>

              {/* Category Filter Chips */}
              <div className="mt-5 flex flex-wrap gap-2">
                {achievementCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedAchievementCategory(cat)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all duration-200 ${
                      selectedAchievementCategory === cat
                        ? "bg-amber-500 text-white shadow-md shadow-amber-500/25 scale-105"
                        : isDarkMode
                        ? "bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Filtered Achievements List */}
              <div className="mt-6 space-y-4">
                {filteredAchievements.slice(0, 3).map((ach) => (
                  <div
                    key={ach.id}
                    className={`group relative overflow-hidden rounded-2xl border p-4 transition-all duration-200 hover:border-amber-500/40 ${
                      isDarkMode
                        ? "border-slate-800/80 bg-slate-950/70 hover:bg-slate-900"
                        : "border-slate-100 bg-slate-50/80 hover:bg-amber-50/30"
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-1.5 font-black text-amber-600 dark:text-amber-400">
                        <FaAward className="text-xs" />
                        <span>{ach.statNumber}</span>
                        <span className="opacity-60">·</span>
                        <span className="font-semibold text-slate-400">{ach.statLabel}</span>
                      </span>
                      <span className="rounded-full bg-slate-200/60 px-2.5 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {ach.year}
                      </span>
                    </div>

                    <h4 className="mt-2 text-sm font-bold tracking-tight text-slate-900 dark:text-white group-hover:text-amber-500 transition-colors">
                      {ach.title}
                    </h4>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                      {ach.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Card: Upcoming Events & Announcements */}
            <div
              className={`rounded-3xl border p-7 sm:p-8 transition-all ${
                isDarkMode
                  ? "border-slate-800/90 bg-[#0d1426] shadow-xl"
                  : "border-slate-200 bg-white shadow-lg"
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500 border border-blue-500/20 shadow-sm">
                  <FaCalendarAlt className="text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-black tracking-tight">
                    Upcoming Events & Announcements
                  </h3>
                  <p className="text-xs text-slate-400">Official circulars & key dates</p>
                </div>
              </div>

              {/* Events List with Calendar Badges */}
              <div className="mt-6 space-y-4">
                {(portalData.events || []).slice(0, 3).map((evt) => {
                  const dateObj = new Date(evt.date);
                  const monthStr = !isNaN(dateObj)
                    ? dateObj.toLocaleString("default", { month: "short" }).toUpperCase()
                    : "DATE";
                  const dayStr = !isNaN(dateObj) ? dateObj.getDate() : "--";

                  return (
                    <div
                      key={evt.id}
                      className={`group flex items-start gap-4 rounded-2xl border p-4 transition-all duration-200 hover:border-blue-500/40 ${
                        isDarkMode
                          ? "border-slate-800/80 bg-slate-950/70 hover:bg-slate-900"
                          : "border-slate-100 bg-slate-50/80 hover:bg-blue-50/30"
                      }`}
                    >
                      {/* Stylized Calendar Date Block */}
                      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-gradient-to-b from-blue-600 to-indigo-700 text-white shadow-md">
                        <span className="text-[10px] font-black uppercase tracking-wider text-blue-200">
                          {monthStr}
                        </span>
                        <span className="text-base font-black leading-none">{dayStr}</span>
                      </div>

                      {/* Event Details */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="rounded-full bg-blue-500/10 px-2.5 py-0.5 text-[10px] font-bold text-blue-600 dark:text-cyan-400 border border-blue-500/20">
                            {evt.badge || evt.category}
                          </span>
                          <span className="text-[11px] text-slate-400 flex items-center gap-1">
                            <FaClock className="text-[9px]" /> {evt.time || evt.date}
                          </span>
                        </div>

                        <h4 className="mt-1.5 text-sm font-bold tracking-tight text-slate-900 dark:text-white group-hover:text-blue-500 transition-colors">
                          {evt.title}
                        </h4>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400 line-clamp-2">
                          {evt.summary}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================
          CAMPUS ADMINISTRATIVE OFFICE & HELPLINE BAR
      ======================================================== */}
      <section className="mx-auto max-w-7xl px-5 py-14 sm:px-6 lg:px-8">
        <div
          className={`relative overflow-hidden rounded-3xl border p-8 shadow-xl ${
            isDarkMode
              ? "border-slate-800 bg-gradient-to-r from-[#0d1426] via-[#101b38] to-[#0d1426]"
              : "border-slate-200 bg-gradient-to-r from-white via-blue-50/50 to-white"
          }`}
        >
          {/* Subtle glowing accents */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-20 -bottom-20 h-48 w-48 rounded-full bg-cyan-500/10 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-8 md:flex-row md:items-center">
            <div>
              <div className="flex items-center gap-2">
                <FaSchool className="text-blue-500" />
                <h3 className="text-lg font-black tracking-tight">Campus Administrative Office</h3>
              </div>
              <p
                className={`mt-2 text-xs sm:text-sm max-w-2xl leading-relaxed ${
                  isDarkMode ? "text-slate-400" : "text-slate-600"
                }`}
              >
                {portalData.campusAddress} · Working Hours: {portalData.officeHours}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-6 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => handleCopy(portalData.contactPhone, "phone")}
                  className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-blue-500 transition-colors"
                  title="Click to copy helpline"
                >
                  <FaPhoneAlt className="text-blue-500" />
                  <span>
                    Helpline: {portalData.contactPhone}
                    {copiedText === "phone" && (
                      <span className="ml-2 text-emerald-400 font-bold">✓ Copied!</span>
                    )}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleCopy(portalData.contactEmail, "email")}
                  className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-cyan-500 transition-colors"
                  title="Click to copy email"
                >
                  <FaEnvelope className="text-cyan-500" />
                  <span>
                    {portalData.contactEmail}
                    {copiedText === "email" && (
                      <span className="ml-2 text-emerald-400 font-bold">✓ Copied!</span>
                    )}
                  </span>
                </button>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-4">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 px-7 py-3.5 text-xs font-black uppercase tracking-wider text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:from-blue-700 hover:to-cyan-700 hover:shadow-cyan-500/35 active:scale-[0.98]"
              >
                <span>Member Sign In →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
